<script setup lang="ts">
/**
 * DefenseStats — HP, EHP summary, with ResistTable and RepRates
 */
import type { DefenseStats as DefenseStatsType } from '../../../shared/types';
import ResistTable from './ResistTable.vue';
import RepRates from './RepRates.vue';

const props = defineProps<{
  defense: DefenseStatsType;
}>();

function fmt(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toFixed(0);
}
</script>

<template>
  <div class="defense-stats">
    <!-- HP summary -->
    <div class="hp-summary">
      <div class="hp-row">
        <span class="hp-label shield">Shield</span>
        <span class="hp-value">{{ fmt(props.defense.shield.hp) }}</span>
      </div>
      <div class="hp-row">
        <span class="hp-label armor">Armor</span>
        <span class="hp-value">{{ fmt(props.defense.armor.hp) }}</span>
      </div>
      <div class="hp-row">
        <span class="hp-label hull">Hull</span>
        <span class="hp-value">{{ fmt(props.defense.hull.hp) }}</span>
      </div>
      <div class="hp-row total">
        <span class="hp-label">Total HP</span>
        <span class="hp-value">{{ fmt(props.defense.total.hp) }}</span>
      </div>
      <div class="hp-row ehp" title="Effective Hit Points">
        <span class="hp-label">EHP</span>
        <span class="hp-value ehp-value">{{ fmt(props.defense.total.ehp) }}</span>
      </div>
    </div>

    <!-- Resist table -->
    <ResistTable :defense="props.defense" />

    <!-- Repair rates -->
    <RepRates :defense="props.defense" />
  </div>
</template>

<style scoped>
.defense-stats {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hp-summary {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hp-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.hp-row.total {
  border-top: 1px solid rgba(42, 63, 95, 0.3);
  padding-top: 3px;
  margin-top: 2px;
}

.hp-row.ehp {
  padding-top: 1px;
}

.hp-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.hp-label.shield { color: var(--shield-color); }
.hp-label.armor { color: var(--armor-color); }
.hp-label.hull { color: var(--hull-color); }

.hp-value {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.ehp-value {
  color: var(--accent-blue);
  font-weight: 600;
}
</style>
